import {
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Select,
  Text,
  VStack,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { FaTrashAlt } from "react-icons/fa";
import * as y from "yup";
import { ReportMode } from "src/Manager/EventDetail/Nav/Reporting/ReportsForm";
import { KYDReportDay } from "@common/interfaces/KYDEvent";
import { useState, useEffect } from "react";
import { AddIcon } from "@chakra-ui/icons";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import actions from "src/actions";

const DEFAULT_RECIPIENT = { email: "" };
const DAYS_OF_WEEK = [
  { label: "Monday", value: "MON" },
  { label: "Tuesday", value: "TUE" },
  { label: "Wednesday", value: "WED" },
  { label: "Thursday", value: "THU" },
  { label: "Friday", value: "FRI" },
  { label: "Saturday", value: "SAT" },
  { label: "Sunday", value: "SUN" },
];

const modes = Object.values(ReportMode);

const emailSchema = y.string().email().required();
const recipientSchema = y.object({ email: emailSchema });

const schema = y.object({
  recipients: y.array(recipientSchema).required(),
  mode: y.mixed<ReportMode>().oneOf(modes).required(),
  custom_days: y.array(y.string()).when("mode", {
    is: (m: ReportMode) => m === ReportMode.CUSTOM,
    then: (s) => s.min(1, "Select at least one day").required(),
    otherwise: (s) => s.optional(),
  }),
  report_time: y.string().optional(),
});

type FormValues = y.InferType<typeof schema>;

const defaultValues: FormValues = {
  recipients: [DEFAULT_RECIPIENT],
  mode: ReportMode.WEEKLY,
  custom_days: [],
  report_time: "08:00",
};

const AdsReportForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    criteriaMode: "all",
    mode: "onChange",
    defaultValues,
  });

  const { append, fields, remove } = useFieldArray({ control, name: "recipients" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await actions.ADMIN.fetchAdsReportRecipients();
        if (cancelled) return;
        const recipients = (data?.recipients ?? []) as string[];
        reset({
          recipients:
            recipients.length > 0
              ? recipients.map((r) => ({ email: r }))
              : [DEFAULT_RECIPIENT],
          mode: (data?.mode as ReportMode) ?? ReportMode.WEEKLY,
          custom_days: (data?.custom_days as string[]) ?? [],
          report_time: data?.report_time ?? "08:00",
        });
      } catch {
        if (!cancelled) reset(defaultValues);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      await actions.ADMIN.createAdsReportRecipients({
        mode: values.mode,
        recipients: values.recipients.map((r) => r.email),
        custom_days: values.mode === ReportMode.CUSTOM ? (values.custom_days as KYDReportDay[]) : undefined,
        report_time: values.report_time ?? "08:00",
      });
      reset(values);
    } finally {
      setIsLoading(false);
    }
  };

  const mode = watch("mode");
  const report_time = watch("report_time");
  const custom_days = watch("custom_days");

  if (initialLoading) {
    return null;
  }

  return (
    <VStack w="full" my={4} alignItems="flex-start" gap={6}>
      <Heading size="sm" py={2}>
        Meta Ads Report Emails
      </Heading>
      <Text fontSize="sm" color="gray.600">
        Weekly (or custom) email with campaign performance and optimization suggestions.
      </Text>
      <HStack w="full" gap={4}>
        <Text fontWeight="medium">Frequency</Text>
        <Select {...register("mode")} isDisabled={isLoading} maxW="200px">
          {modes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
      </HStack>

      {mode === ReportMode.CUSTOM && (
        <VStack w="full" alignItems="flex-start" spacing={2}>
          <Text fontWeight="medium">Select days to send reports</Text>
          <Controller
            control={control}
            name="custom_days"
            render={({ field }) => (
              <CheckboxGroup
                value={field.value}
                onChange={(values) => field.onChange(values)}
                isDisabled={isLoading}
              >
                <HStack flexWrap="wrap" spacing={4}>
                  {DAYS_OF_WEEK.map((day) => (
                    <Checkbox
                      key={day.value}
                      value={day.value}
                      sx={{
                        ".chakra-checkbox__control": {
                          borderColor: "black",
                          _checked: { bg: "black", borderColor: "black" },
                        },
                      }}
                    >
                      {day.label}
                    </Checkbox>
                  ))}
                </HStack>
              </CheckboxGroup>
            )}
          />
          {errors.custom_days && (
            <Text color="red.500" fontSize="sm">
              {errors.custom_days.message}
            </Text>
          )}
        </VStack>
      )}

      <VStack w="full" alignItems="flex-start" spacing={0}>
        <Text fontWeight="medium" marginBottom={2}>
          Report time
        </Text>
        <Text fontSize="sm" color="gray.600" marginBottom={2}>
          Time is in the venue timezone.
        </Text>
        <Controller
          control={control}
          name="report_time"
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Time"
                value={field.value ? dayjs(field.value, "HH:mm") : dayjs("08:00", "HH:mm")}
                onChange={(newValue) =>
                  field.onChange(newValue ? newValue.format("HH:mm") : "08:00")
                }
                disabled={isLoading}
                views={["hours", "minutes"]}
                ampm
              />
            </LocalizationProvider>
          )}
        />
      </VStack>

      <Stack w="full" spacing={0}>
        <Text fontWeight="medium">Send reports to</Text>
        <Stack w="full" spacing={2} mt={2}>
          {fields.map((field, index) => (
            <HStack w="full" justifyContent="space-between" key={field.id}>
              <Input
                isDisabled={isLoading}
                isInvalid={!!errors.recipients?.[index]?.email}
                {...register(`recipients.${index}.email`)}
                placeholder="email@example.com"
              />
              <IconButton
                isDisabled={isLoading}
                aria-label="Remove recipient"
                variant="ghost"
                onClick={() => remove(index)}
                icon={<FaTrashAlt fontSize="1rem" />}
              />
            </HStack>
          ))}
        </Stack>
      </Stack>
      <Button
        leftIcon={<AddIcon />}
        size="sm"
        onClick={() => append(DEFAULT_RECIPIENT)}
      >
        Add recipient
      </Button>

      <HStack>
        <Button
          isDisabled={!isDirty || !isValid || isLoading}
          isLoading={isLoading}
          colorScheme="green"
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
        <Button variant="ghost" onClick={() => reset()} isDisabled={!isDirty || isLoading}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  );
};

export default AdsReportForm;
